<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { updateAccount } from '@gpx-spa/queries';
import { Button, GpxMetersDistanceField } from '@gpx-spa/ui';
import HeadingSection from '@/components/_common/Heading/HeadingSection.vue';
import SettingsSettingRow from '@/components/settings/SettingsSettingRow.vue';
import {
  ACCOUNT_DEFAULT_CODE,
  useAccountDefaults,
} from '@/composables/useAccountDefaults';
import {
  COMPOSER_CORRIDOR_BUFFER_DEFAULT_M,
  COMPOSER_CORRIDOR_BUFFER_MAX_M,
  COMPOSER_CORRIDOR_BUFFER_MIN_M,
  COMPOSER_CORRIDOR_BUFFER_STEP_M,
  COMPOSER_STOP_RADIUS_DEFAULT_M,
  COMPOSER_STOP_RADIUS_MAX_M,
  COMPOSER_STOP_RADIUS_MIN_M,
  COMPOSER_STOP_RADIUS_STEP_M,
} from '@/components/shipment/form/domain/constants';
import { COMMON_UPDATE_SUCCESS } from '@/config/constants';
import { setTitle } from '@/utils/title';

const AUTO_ARCHIVE_CODE = 'auto_archive_devices_on_shipment_completion';
const AUTO_ARCHIVE_ERROR = 'Could not update automatic archiving';
const LEARNED_ROUTES_CODE = 'learned_routes_available';
const LEARNED_ROUTES_ERROR = 'Could not update learned routes';

const toast = useToast();
const store = useStore();
const { readNumber, save, isSaving } = useAccountDefaults();
const { mutateAsync: mutateAutoArchive, isPending: isSavingAutoArchive } =
  updateAccount();
const { mutateAsync: mutateLearnedRoutes, isPending: isSavingLearnedRoutes } =
  updateAccount();

const builtIn = {
  [ACCOUNT_DEFAULT_CODE.stopRadiusOrigin]: COMPOSER_STOP_RADIUS_DEFAULT_M,
  [ACCOUNT_DEFAULT_CODE.stopRadiusDestination]: COMPOSER_STOP_RADIUS_DEFAULT_M,
  [ACCOUNT_DEFAULT_CODE.stopRadiusWaypoint]: COMPOSER_STOP_RADIUS_DEFAULT_M,
  [ACCOUNT_DEFAULT_CODE.routeCorridorBuffer]:
    COMPOSER_CORRIDOR_BUFFER_DEFAULT_M,
};

const origin = ref(
  readNumber(ACCOUNT_DEFAULT_CODE.stopRadiusOrigin) ??
    builtIn[ACCOUNT_DEFAULT_CODE.stopRadiusOrigin],
);
const destination = ref(
  readNumber(ACCOUNT_DEFAULT_CODE.stopRadiusDestination) ??
    builtIn[ACCOUNT_DEFAULT_CODE.stopRadiusDestination],
);
const waypoint = ref(
  readNumber(ACCOUNT_DEFAULT_CODE.stopRadiusWaypoint) ??
    builtIn[ACCOUNT_DEFAULT_CODE.stopRadiusWaypoint],
);
const corridor = ref(
  readNumber(ACCOUNT_DEFAULT_CODE.routeCorridorBuffer) ??
    builtIn[ACCOUNT_DEFAULT_CODE.routeCorridorBuffer],
);

const onSave = () =>
  save({
    [ACCOUNT_DEFAULT_CODE.stopRadiusOrigin]: origin.value,
    [ACCOUNT_DEFAULT_CODE.stopRadiusDestination]: destination.value,
    [ACCOUNT_DEFAULT_CODE.stopRadiusWaypoint]: waypoint.value,
    [ACCOUNT_DEFAULT_CODE.routeCorridorBuffer]: corridor.value,
  })
    .then(() => toast.success(COMMON_UPDATE_SUCCESS))
    .catch(() => toast.error('Could not update shipment defaults'));

const onReset = () =>
  save({
    [ACCOUNT_DEFAULT_CODE.stopRadiusOrigin]: null,
    [ACCOUNT_DEFAULT_CODE.stopRadiusDestination]: null,
    [ACCOUNT_DEFAULT_CODE.stopRadiusWaypoint]: null,
    [ACCOUNT_DEFAULT_CODE.routeCorridorBuffer]: null,
  })
    .then(() => {
      origin.value = builtIn[ACCOUNT_DEFAULT_CODE.stopRadiusOrigin];
      destination.value = builtIn[ACCOUNT_DEFAULT_CODE.stopRadiusDestination];
      waypoint.value = builtIn[ACCOUNT_DEFAULT_CODE.stopRadiusWaypoint];
      corridor.value = builtIn[ACCOUNT_DEFAULT_CODE.routeCorridorBuffer];
      toast.success(COMMON_UPDATE_SUCCESS);
    })
    .catch(() => toast.error('Could not update shipment defaults'));

const account = computed(() => store.getters['auth/account']);
const isAutoArchiveOn = computed(() =>
  store.getters['auth/accountSettingIsOn'](AUTO_ARCHIVE_CODE),
);
const isLearnedRoutesOn = computed(
  () => store.getters['auth/learnedRoutesAvailable'],
);

// Not a default, so it is not part of the batch above: it saves the moment it is
// flipped, and the confirmation is where the customer learns what happens from here
// on, since nothing happens at the moment of the toggle. That modal reports the
// result of the promise it is handed, so this one must be left free to reject.
const saveAutoArchive = (value: boolean) =>
  mutateAutoArchive({
    accountId: account.value.id,
    payload: { settings: { [AUTO_ARCHIVE_CODE]: value } },
  }).then((updatedAccount: { settings: unknown }) =>
    store.dispatch('auth/setAccountSettings', {
      accountId: account.value.id,
      settings: updatedAccount.settings,
    }),
  );

const saveLearnedRoutes = (value: boolean) =>
  mutateLearnedRoutes({
    accountId: account.value.id,
    payload: { settings: { [LEARNED_ROUTES_CODE]: value } },
  }).then((updatedAccount: { settings: unknown }) =>
    store.dispatch('auth/setAccountSettings', {
      accountId: account.value.id,
      settings: updatedAccount.settings,
    }),
  );

const onToggleLearnedRoutes = (value: boolean) => {
  saveLearnedRoutes(value)
    .then(() => toast.success(COMMON_UPDATE_SUCCESS))
    .catch(() => toast.error(LEARNED_ROUTES_ERROR));
};

const onToggleAutoArchive = (value: boolean) => {
  if (!value) {
    saveAutoArchive(false)
      .then(() => toast.success(COMMON_UPDATE_SUCCESS))
      .catch(() => toast.error(AUTO_ARCHIVE_ERROR));
    return;
  }

  store.dispatch('modal/open', {
    name: 'CommonConfirmationModal',
    params: {
      actionLabel: 'Turn on',
      title: 'Archive trackers when a shipment is delivered',
      body: 'From now on, a tracker is archived once the shipment it is on is delivered — unless it is still on another shipment. Shipments already delivered are left alone, and an archived tracker can be restored for 10 days.',
      onOk: () => saveAutoArchive(true),
      successMessage: COMMON_UPDATE_SUCCESS,
      errorMessage: AUTO_ARCHIVE_ERROR,
    },
  });
};

setTitle('Shipment Settings');
</script>

<template>
  <section class="max-w-[860px]">
    <HeadingSection
      class="mb-[35px]"
      desc="Starting values for new shipments. Anyone can still change any value on an individual shipment."
      heading="Shipments"
    />
    <SettingsSettingRow
      wide
      title="Origin radius"
      desc="Area around the pickup that starts the shipment"
    >
      <GpxMetersDistanceField
        class="w-full"
        v-model="origin"
        title=""
        size="small"
        preserve-parent-model-on-mount
        :min-meters="COMPOSER_STOP_RADIUS_MIN_M"
        :max-meters="COMPOSER_STOP_RADIUS_MAX_M"
        :step-meters="COMPOSER_STOP_RADIUS_STEP_M"
      />
    </SettingsSettingRow>
    <SettingsSettingRow
      wide
      title="Destination radius"
      desc="Area around the drop-off that marks it delivered"
    >
      <GpxMetersDistanceField
        class="w-full"
        v-model="destination"
        title=""
        size="small"
        preserve-parent-model-on-mount
        :min-meters="COMPOSER_STOP_RADIUS_MIN_M"
        :max-meters="COMPOSER_STOP_RADIUS_MAX_M"
        :step-meters="COMPOSER_STOP_RADIUS_STEP_M"
      />
    </SettingsSettingRow>
    <SettingsSettingRow
      wide
      title="Waypoint radius"
      desc="Area around each intermediate stop"
    >
      <GpxMetersDistanceField
        class="w-full"
        v-model="waypoint"
        title=""
        size="small"
        preserve-parent-model-on-mount
        :min-meters="COMPOSER_STOP_RADIUS_MIN_M"
        :max-meters="COMPOSER_STOP_RADIUS_MAX_M"
        :step-meters="COMPOSER_STOP_RADIUS_STEP_M"
      />
    </SettingsSettingRow>
    <SettingsSettingRow
      wide
      title="Route corridor width"
      desc="How far off the planned route counts as a deviation"
    >
      <GpxMetersDistanceField
        class="w-full"
        v-model="corridor"
        title=""
        size="small"
        preserve-parent-model-on-mount
        :min-meters="COMPOSER_CORRIDOR_BUFFER_MIN_M"
        :max-meters="COMPOSER_CORRIDOR_BUFFER_MAX_M"
        :step-meters="COMPOSER_CORRIDOR_BUFFER_STEP_M"
      />
    </SettingsSettingRow>
    <div
      class="mt-[24px] flex items-center gap-x-[16px] border-t border-t-gpx_gray-800 pt-[24px]"
    >
      <Button
        class="w-48"
        :loading="isSaving"
        loadingText="Saving..."
        :disabled="isSaving"
        @click="onSave"
      >
        Save defaults
      </Button>
      <button
        class="text-sm text-gpx_gray-100 hover:text-gpx_black-200"
        :disabled="isSaving"
        @click="onReset"
      >
        Reset to built-in defaults
      </button>
    </div>
    <p
      class="mt-[16px] max-w-[58ch] border-l-2 border-l-gpx_gray-700 pl-[11px] text-xs text-gpx_gray-200"
    >
      Changing a default affects shipments created from now on. Existing
      shipments are never modified.
    </p>
    <HeadingSection
      class="mb-[35px] mt-[48px]"
      desc="Route options when you create or edit a shipment."
      heading="Route options"
    />
    <SettingsSettingRow
      title="Learned Routes"
      desc="Suggest routes based on your account's completed shipments on the same lane, alongside Google's options."
    >
      <div class="flex items-center gap-x-[10px] sm:justify-end">
        <UISpinnerNew v-if="isSavingLearnedRoutes" size="sm" />
        <UIToggle
          :modelValue="isLearnedRoutesOn"
          :disabled="isSavingLearnedRoutes"
          aria-label="Learned Routes"
          @update:modelValue="onToggleLearnedRoutes"
        />
      </div>
    </SettingsSettingRow>
    <HeadingSection
      class="mb-[35px] mt-[48px]"
      desc="What the platform does when a shipment finishes. This one is not a starting value — it takes effect on its own."
      heading="On delivery"
    />
    <SettingsSettingRow
      title="Archive this shipment's trackers"
      desc="For single-use trackers. A tracker still on another shipment is left alone, and archiving can be undone for 10 days."
    >
      <div class="flex items-center gap-x-[10px] sm:justify-end">
        <UISpinnerNew v-if="isSavingAutoArchive" size="sm" />
        <UIToggle
          :modelValue="isAutoArchiveOn"
          :disabled="isSavingAutoArchive"
          aria-label="Archive this shipment's trackers on delivery"
          @update:modelValue="onToggleAutoArchive"
        />
      </div>
    </SettingsSettingRow>
  </section>
</template>
